// layouts/SoloLayout.jsx
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  EventNote as EventNoteIcon,
  EventAvailable as EventAvailableIcon,
  RoomService as RoomServiceIcon,
  People as PeopleIcon, 
  Calculate as CalculateIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  DirectionsCar as DirectionsCarIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AccountBalance as AccountBalanceIcon,
  AddCircle as AddCircleIcon,
  ShoppingCart as ShoppingCartIcon,
  Event as EventIcon,
  Inventory2 as Inventory2Icon
} from '@mui/icons-material';

const DRAWER_WIDTH = 220;

const SoloLayout = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuSections = [
    {
      title: 'Main',
      items: [
        {
          text: 'Dashboard',
          icon: <DashboardIcon />,
          path: '/solo-dashboard',
          description: 'Overview & tax summary'
        },
        {
          text: 'Appointments',
          icon: <EventNoteIcon />,
          path: '/solo/bookings',
          description: 'Manage your diary'
        },
        {
          text: 'Online Bookings',
          icon: <EventAvailableIcon />,
          path: '/solo/online-bookings',
          description: 'Public booking enquiries'
        },
        {
          text: 'Clients',
          icon: <PeopleIcon />,
          path: '/solo/clients',
          description: 'Client & patch test records'
        },
        {
          text: 'Services',
          icon: <RoomServiceIcon />,
          path: '/solo/services',
          description: 'Service management'
        },
        {
          text: 'Packages',
          icon: <Inventory2Icon />,
          path: '/solo/packages',
          description: 'Bundle services together'
        }
      ]
    },
    {
      title: 'Finance',
      items: [
        {
          text: 'Record Sale',
          icon: <AddCircleIcon />,
          path: '/solo/record-sale',
          description: 'Log income'
        },
        {
          text: 'Add Expense',
          icon: <ReceiptIcon />,
          path: '/solo/add-expense',
          description: 'Track costs'
        },
        {
          text: 'Log Mileage',
          icon: <DirectionsCarIcon />,
          path: '/solo/log-mileage',
          description: 'Business travel'
        }
      ]
    },
    {
      title: 'Tools',
      items: [
        {
          text: 'Calculator',
          icon: <CalculateIcon />,
          path: '/solo/calculator',
          description: 'Service pricing'
        },
        {
          text: 'Booking Page',
          icon: <EventIcon />,
          path: '/solo/booking-settings',
          description: 'Online booking setup'
        },
        {
          text: 'Shopping list',
          icon: <ShoppingCartIcon />,
          path: '/solo/shopping-list',
          description: 'Stock reorder alerts'
        },
        {
          text: 'Reports',
          icon: <AssessmentIcon />,
          path: '/solo-reports',
          description: 'Business analytics'
        },
        {
          text: 'Import Data',
          icon: <CloudUploadIcon />,
          path: '/solo/import-data',
          description: 'Bulk upload'
        }
      ]
    },
    {
      title: 'Tax',
      items: [
        {
          text: 'Self Assessment',
          icon: <DescriptionIcon />,
          path: '/solo/self-assessment',
          description: 'MTD tax return'
        },
        {
          text: 'Tax Settings',
          icon: <AccountBalanceIcon />,
          path: '/solo/settings',
          description: 'UTR, NINO, VAT'
        }
      ]
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    navigate('/');
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo/Brand */}
      <Box 
        sx={{ 
          p: 2,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'primary.main',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Box
          sx={{
            width: 100,
            height: 100,
            bgcolor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            boxShadow: 2
          }}
        >
          <Typography 
            variant="subtitle2" 
            color="primary" 
            fontWeight="bold"
            sx={{ fontSize: '0.65rem', textAlign: 'center', px: 1 }}
          >
            SALONLOGIC
            <br />
            PRO
          </Typography>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflowY: 'auto', pt: 1 }}>
        {menuSections.map((section, sectionIdx) => (
          <Box key={section.title} sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: 0.5
              }}
            >
              {section.title}
            </Typography>
            <List sx={{ py: 0, px: 1 }}>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      selected={isActive}
                      sx={{
                        borderRadius: 1,
                        py: 0.75,
                        px: 1.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark'
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white'
                          }
                        }
                      }}
                    >
                      <ListItemIcon 
                        sx={{ 
                          color: isActive ? 'white' : 'inherit',
                          minWidth: 32 
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text}
                        secondary={!isActive ? item.description : null}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 400,
                          fontSize: '0.8rem'
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.65rem'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            {sectionIdx < menuSections.length - 1 && (
              <Divider sx={{ my: 1, mx: 2 }} />
            )}
          </Box>
        ))}
      </Box>

      <Divider />

      {/* User Profile & Logout */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Avatar 
            sx={{ 
              bgcolor: 'primary.main', 
              width: 36, 
              height: 36,
              mr: 1.5,
              fontSize: '0.9rem'
            }}
          >
            A
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
              Andrew
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Solo Trader
            </Typography>
          </Box>
          <IconButton 
            size="small"
            onClick={() => handleNavigation('/solo/settings')}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Box>

        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            color: 'error.main',
            py: 0.75,
            '&:hover': {
              bgcolor: 'error.lighter'
            }
          }}
        >
          <ListItemIcon sx={{ color: 'error.main', minWidth: 36 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{
              fontSize: '0.875rem'
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          color="inherit"
          edge="start"
          onClick={() => setMobileOpen(!mobileOpen)}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: theme.zIndex.drawer + 2,
            bgcolor: 'white',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'grey.100'
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider'
          }
        }}
        ModalProps={{
          keepMounted: true
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: '#f5f5f5'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default SoloLayout;